import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import { useMemo } from "react";

import {
  getOptimisticValue,
  useIsProcessing,
  useLastProcessedRequest,
  useQueue,
} from "@v2/features/modifiers/store/ModifierQueueStoreContext";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";

/**
 * Returns the current value for a modifier, preferring optimistic values from
 * the queue over the selected element's current value.
 *
 * Priority order:
 * 1. Optimistic value from queue (if exists)
 * 2. Extracted value from selected element (using extractor function)
 * 3. undefined (if no element selected)
 *
 * @example
 * // For simple modifiers like fontSize
 * const currentFontSize = useOptimisticModifierValue({
 *   modifierType: { type: "fontSize", value: 0 },
 *   extractor: (elementDesignation) =>
 *     elementDesignation.element.styleOverrides?.fontSize ?? null,
 * });
 *
 * @example
 * // For discriminated modifiers like paddingTop
 * const currentPaddingTop = useOptimisticModifierValue({
 *   modifierType: { type: "padding", direction: "top", value: 0 },
 *   extractor: (elementDesignation) =>
 *     elementDesignation.element.styleOverrides?.paddingTop ?? undefined,
 * });
 */
export function useOptimisticModifierValue<ExtractorReturnType>(opts: {
  modifierType: UniversalModifierValue;
  extractor: (
    element: NonNullable<
      ReturnType<typeof useDefaultSelectedElementDesignation>
    >,
  ) => ExtractorReturnType;
}): ExtractorReturnType | undefined {
  const { modifierType, extractor } = opts;
  const selectedElement = useDefaultSelectedElementDesignation();
  const queue = useQueue();
  const isProcessing = useIsProcessing();
  const lastProcessedRequest = useLastProcessedRequest();

  const result = useMemo(() => {
    if (!selectedElement) {
      return undefined;
    }

    const stableElementId = selectedElement.element.stableElementId;

    const optimisticModifier = getOptimisticValue({
      queue,
      lastProcessedRequest,
      isProcessing,
      stableElementId,
      modifierType,
    });

    if (optimisticModifier && "value" in optimisticModifier) {
      return optimisticModifier.value as ExtractorReturnType;
    }

    const fallbackValue = extractor(selectedElement);
    return fallbackValue;
  }, [
    selectedElement,
    modifierType,
    queue,
    lastProcessedRequest,
    isProcessing,
    extractor,
  ]);

  return result;
}
